import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * The objections a buyer raises out loud, answered on the page.
 *
 * Modelled on how PointClickCare's Transitions IQ page handles the same job:
 * the FAQ answers what a skeptical buyer actually says ("our EHR already does
 * this"), not what a marketer wishes they would ask.
 *
 * Every answer here is deliberately checkable. Nothing on this page claims a
 * capability that is not live today — where something is in progress, it says
 * so, because a buyer who finds one overstated answer re-reads all the others.
 */
export interface QA {
  q: string;
  a: React.ReactNode;
}

/** Asked on every Solutions page, because every buyer asks them. */
export const SHARED_QUESTIONS: QA[] = [
  {
    q: 'How is this different from a resource directory?',
    a: (
      <>
        A directory tells you an organization exists. It stops at the referral. WeaveIn
        carries the same record through what happened next — whether the person was
        reached, what was delivered, and by whom — and keeps that record with the
        organization that did the work.
      </>
    ),
  },
  {
    q: 'What do the community organizations have to do?',
    a: (
      <>
        Use it on the device they already carry. A community health worker records a
        visit on a phone or tablet, in English or Spanish, in the field. There is no
        second system to keep in step and no separate portal per referring partner.
      </>
    ),
  },
  {
    q: 'Do the community organizations get paid?',
    a: (
      <>
        That is the point of capturing the encounter properly. The work only bills if it
        is documented against the right program with the right codes, which is what the
        platform is for. Payment itself still runs through each funder&rsquo;s own
        contract and channel.
      </>
    ),
  },
  {
    q: 'How long does it take to go live?',
    a: (
      <>
        You are not starting from a blank system. We set up your organization, programs
        and users with you; forms, screenings and referral workflows come from working
        programs, so you adapt them rather than author them; and your team reaches a
        person who knows your configuration.
      </>
    ),
  },
  {
    q: 'Where does the data live, and is it HIPAA compliant?',
    a: (
      <>
        Yes, HIPAA compliant, with each organization&rsquo;s data isolated from every
        other organization on the platform. Consent is recorded per member and per
        exchange. See{' '}
        <a href="/security" className="text-v2accent hover:underline">
          Security
        </a>{' '}
        for the security posture, and{' '}
        <a href="/developers" className="text-v2accent hover:underline">
          Developers
        </a>{' '}
        for the standards and integration detail.
      </>
    ),
  },
];

interface CommonQuestionsProps {
  /** Page-specific questions, shown above the shared ones. */
  questions?: QA[];
  heading?: string;
}

const CommonQuestions: React.FC<CommonQuestionsProps> = ({
  questions = [],
  heading = 'Questions people ask',
}) => {
  const all = [...questions, ...SHARED_QUESTIONS];
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-16 bg-white dark:bg-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-8 text-center">
          {heading}
        </h2>
        <div className="divide-y divide-gray-200 dark:divide-gray-800 border-y border-gray-200 dark:border-gray-800">
          {all.map((item, i) => (
            <div key={item.q}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between text-left py-5 gap-4"
              >
                <span className="font-semibold text-gray-800 dark:text-gray-100">
                  {item.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${
 open === i ? 'rotate-180' : ''
 }`}
                />
              </button>
              {open === i && (
                <p className="pb-5 -mt-1 text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CommonQuestions;
